import { useRef, useState, useEffect, type KeyboardEvent, type ClipboardEvent } from 'react';
import { cn } from '@/utils/cn';

export interface MfaCodeInputProps {
  length?: number;
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  disabled?: boolean;
  error?: string;
  autoFocus?: boolean;
  className?: string;
}

/**
 * MFA Code Input Component
 * 6-digit code input with auto-focus between digits
 * Accessible with keyboard navigation and screen reader support
 */
export default function MfaCodeInput({
  length = 6,
  value,
  onChange,
  onComplete,
  disabled = false,
  error,
  autoFocus = true,
  className,
}: MfaCodeInputProps) {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [focusedIndex, setFocusedIndex] = useState(0);

  // Split value into array of digits
  const digits = value.split('').concat(Array(length).fill('')).slice(0, length);

  // Auto-focus first input on mount
  useEffect(() => {
    if (autoFocus && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [autoFocus]);

  // Call onComplete when all digits are entered
  useEffect(() => {
    if (value.length === length && onComplete) {
      onComplete(value);
    }
  }, [value, length, onComplete]);

  const focusInput = (index: number) => {
    const safeIndex = Math.max(0, Math.min(index, length - 1));
    inputRefs.current[safeIndex]?.focus();
    setFocusedIndex(safeIndex);
  };

  const handleChange = (index: number, newValue: string) => {
    // Only allow digits
    const digit = newValue.replace(/\D/g, '').slice(-1);

    if (digit) {
      const newDigits = [...digits];
      newDigits[index] = digit;
      const newCode = newDigits.join('').slice(0, length);
      onChange(newCode);

      // Move to next input
      if (index < length - 1) {
        focusInput(index + 1);
      }
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'Backspace':
        e.preventDefault();
        if (digits[index]) {
          // Clear current digit
          const newDigits = [...digits];
          newDigits[index] = '';
          onChange(newDigits.join(''));
        } else if (index > 0) {
          // Move to previous input and clear it
          const newDigits = [...digits];
          newDigits[index - 1] = '';
          onChange(newDigits.join(''));
          focusInput(index - 1);
        }
        break;
      case 'ArrowLeft':
        e.preventDefault();
        if (index > 0) {
          focusInput(index - 1);
        }
        break;
      case 'ArrowRight':
        e.preventDefault();
        if (index < length - 1) {
          focusInput(index + 1);
        }
        break;
      case 'Delete': {
        e.preventDefault();
        const newDigits = [...digits];
        newDigits[index] = '';
        onChange(newDigits.join(''));
        break;
      }
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    if (pastedData) {
      onChange(pastedData);
      // Focus the input after the last pasted digit
      const nextIndex = Math.min(pastedData.length, length - 1);
      focusInput(nextIndex);
    }
  };

  const handleFocus = (index: number) => {
    setFocusedIndex(index);
    // Select the input value when focused
    inputRefs.current[index]?.select();
  };

  return (
    <div className={cn('flex flex-col items-center', className)}>
      <div
        className="flex gap-2 sm:gap-3"
        role="group"
        aria-label="Verification code input"
      >
        {digits.map((digit, index) => (
          <input
            key={index}
            ref={(el) => {
              inputRefs.current[index] = el;
            }}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={1}
            value={digit}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            onFocus={() => handleFocus(index)}
            disabled={disabled}
            aria-label={`Digit ${index + 1} of ${length}`}
            aria-invalid={error ? 'true' : 'false'}
            className={cn(
              'w-10 h-12 sm:w-12 sm:h-14 text-center text-xl sm:text-2xl font-semibold',
              'border rounded-lg shadow-sm',
              'focus:outline-none focus:ring-2 focus:ring-offset-1',
              'disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed',
              'transition-colors duration-150',
              error
                ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                : focusedIndex === index
                  ? 'border-primary-500 ring-2 ring-primary-500'
                  : 'border-gray-300 focus:ring-primary-500 focus:border-primary-500'
            )}
          />
        ))}
      </div>
      {error && (
        <p className="mt-2 text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
